import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import {
  API_ROUTES,
  type Anomaly,
  type AuthResponse,
  type AuthUser,
  type CreateOrgDto,
  type CreateProjectDto,
  type CreateSuperAdminDto,
  type CreateUserDto,
  type GeoPolygon,
  type InvestigateDto,
  type InvestigationResult,
  type KpiSummary,
  type Organization,
  type OrganizationsResponse,
  type Project,
  type ProjectsResponse,
  type RulesResponse,
  type RunDetectionDto,
  type TowersResponse,
  type UpdateOrgDto,
  type UpdateProjectDto,
  type UpdateSuperAdminDto,
  type UpdateUserDto,
  type User,
  type UsersResponse,
} from '@soc/shared';

const API_URL = import.meta.env.VITE_API_URL ?? '/api';

/**
 * Serialize query params for a GET, JSON-encoding any polygon so it survives the
 * query string (the backend parses it back into a GeoPolygon).
 */
function withPolygon<T extends { polygon?: GeoPolygon }>(
  params: T,
): Record<string, unknown> {
  const { polygon, ...rest } = params;
  return polygon ? { ...rest, polygon: JSON.stringify(polygon) } : { ...rest };
}

const rawBaseQuery = fetchBaseQuery({ baseUrl: API_URL, credentials: 'include' });

/**
 * Single-flight refresh shared across concurrent 401s. When the short-lived
 * access cookie has expired, one silent `POST /auth/refresh` rotates the refresh
 * token and re-issues the session; the original request is then retried. If
 * refresh also fails, the 401 propagates and the app treats the user as logged
 * out (the `Auth` cache clears → ProtectedRoute redirects to /login).
 */
type RawResult = Awaited<ReturnType<typeof rawBaseQuery>>;
let refreshPromise: Promise<RawResult> | null = null;

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  const url = typeof args === 'string' ? args : args.url;

  if (result.error?.status === 401 && url !== API_ROUTES.auth.refresh) {
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          return await rawBaseQuery({ url: API_ROUTES.auth.refresh, method: 'POST' }, api, extraOptions);
        } finally {
          refreshPromise = null;
        }
      })();
    }
    const refreshResult = await refreshPromise;
    if (!refreshResult.error) {
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }
  return result;
};

/**
 * Single RTK Query client for all REST endpoints. Detection is stateless
 * request/response — the frontend engine drives per-rule ticks and owns all
 * alert state (see features/detection); there is no SSE.
 */
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Rules', 'Kpis', 'Projects', 'Auth', 'Users', 'Organizations'],
  endpoints: (build) => ({
    // --- auth ---------------------------------------------------------------
    getMe: build.query<AuthUser, void>({
      query: () => API_ROUTES.auth.me,
      providesTags: ['Auth'],
    }),
    logout: build.mutation<{ ok: true }, void>({
      query: () => ({ url: API_ROUTES.auth.logout, method: 'POST' }),
      invalidatesTags: ['Auth', 'Projects', 'Users', 'Organizations'],
    }),
    refreshSession: build.mutation<AuthResponse, void>({
      query: () => ({ url: API_ROUTES.auth.refresh, method: 'POST' }),
      invalidatesTags: ['Auth'],
    }),

    // --- detection / data ---------------------------------------------------
    getRules: build.query<RulesResponse, void>({
      query: () => '/rules',
      providesTags: ['Rules'],
    }),
    getTowers: build.query<TowersResponse, { polygon?: GeoPolygon } | void>({
      query: (params) => ({ url: '/towers', params: withPolygon(params ?? {}) }),
    }),
    getKpis: build.query<KpiSummary, void>({
      query: () => '/stats/kpis',
      providesTags: ['Kpis'],
    }),
    // Stateless detection: run ONE rule over ONE window, return the anomalies.
    // Modeled as a mutation because it's imperative + uncached — a query would
    // mint a new cache key every tick. The detection engine calls this per tick
    // and owns all alert state, so it touches no tags.
    runDetection: build.mutation<Anomaly[], RunDetectionDto>({
      query: (body) => ({ url: API_ROUTES.detectionRun, method: 'POST', body }),
    }),
    // Drill down into ONE anomaly — re-query the site/session behind it and return
    // structured evidence. A mutation (imperative + uncached, like runDetection);
    // the Investigate panel fires it on demand and caches the result by anomaly id.
    investigate: build.mutation<InvestigationResult, InvestigateDto>({
      query: (body) => ({ url: API_ROUTES.detectionInvestigate, method: 'POST', body }),
    }),

    // --- projects -----------------------------------------------------------
    getProjects: build.query<ProjectsResponse, void>({
      query: () => '/projects',
      providesTags: ['Projects'],
    }),
    createProject: build.mutation<Project, CreateProjectDto>({
      query: (body) => ({ url: '/projects', method: 'POST', body }),
      invalidatesTags: ['Projects'],
    }),
    updateProject: build.mutation<Project, { id: string } & UpdateProjectDto>({
      query: ({ id, ...body }) => ({ url: `/projects/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Projects'],
    }),
    deleteProject: build.mutation<void, string>({
      query: (id) => ({ url: `/projects/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Projects'],
    }),

    // --- users --------------------------------------------------------------
    getUsers: build.query<UsersResponse, void>({
      query: () => API_ROUTES.users,
      providesTags: ['Users'],
    }),
    createUser: build.mutation<User, CreateUserDto>({
      query: (body) => ({ url: API_ROUTES.users, method: 'POST', body }),
      invalidatesTags: ['Users'],
    }),
    updateUser: build.mutation<User, { id: string } & UpdateUserDto>({
      query: ({ id, ...body }) => ({ url: API_ROUTES.user(id), method: 'PATCH', body }),
      invalidatesTags: ['Users'],
    }),
    deleteUser: build.mutation<void, string>({
      query: (id) => ({ url: API_ROUTES.user(id), method: 'DELETE' }),
      invalidatesTags: ['Users'],
    }),
    // Dedicated super_admin management (super_admin-only, org-less).
    createSuperAdmin: build.mutation<User, CreateSuperAdminDto>({
      query: (body) => ({ url: API_ROUTES.superAdmins, method: 'POST', body }),
      invalidatesTags: ['Users'],
    }),
    updateSuperAdmin: build.mutation<User, { id: string } & UpdateSuperAdminDto>({
      query: ({ id, ...body }) => ({ url: API_ROUTES.superAdmin(id), method: 'PATCH', body }),
      invalidatesTags: ['Users'],
    }),

    // --- organizations ------------------------------------------------------
    getOrganizations: build.query<OrganizationsResponse, void>({
      query: () => API_ROUTES.organizations,
      providesTags: ['Organizations'],
    }),
    // The caller's own org — org-scoped members use this to read their coverage
    // boundary + budget (super_admin reads the full list instead, so gets null here).
    getMyOrganization: build.query<Organization | null, void>({
      query: () => API_ROUTES.myOrganization,
      providesTags: ['Organizations'],
    }),
    createOrganization: build.mutation<Organization, CreateOrgDto>({
      query: (body) => ({ url: API_ROUTES.organizations, method: 'POST', body }),
      invalidatesTags: ['Organizations'],
    }),
    updateOrganization: build.mutation<Organization, { id: string } & UpdateOrgDto>({
      query: ({ id, ...body }) => ({ url: API_ROUTES.organization(id), method: 'PATCH', body }),
      invalidatesTags: ['Organizations'],
    }),
    deleteOrganization: build.mutation<void, string>({
      query: (id) => ({ url: API_ROUTES.organization(id), method: 'DELETE' }),
      // Deleting an org can change which projects are visible.
      invalidatesTags: ['Organizations', 'Projects'],
    }),
  }),
});

export const {
  useGetMeQuery,
  useLogoutMutation,
  useRefreshSessionMutation,
  useGetRulesQuery,
  useGetTowersQuery,
  useGetKpisQuery,
  useRunDetectionMutation,
  useInvestigateMutation,
  useGetProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useCreateSuperAdminMutation,
  useUpdateSuperAdminMutation,
  useGetOrganizationsQuery,
  useGetMyOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
} = api;
